import { useMemo } from "react"
import { keepPreviousData } from "@tanstack/react-query"

import { useAllocationInTransitMap } from "~/hooks/useAllocationInTransitMap"
import { useAllocationInventoryMap } from "~/hooks/useAllocationInventoryMap"
import { useAllocationSkuExtraDataMap } from "~/hooks/useAllocationSkuExtraDataMap"
import { useAllocationSalesMap } from "~/hooks/useAllocationSalesMap"
import { useAllocationStoreMap } from "~/hooks/useAllocationStoreMap"
import { useAllocationWarehouseMap } from "~/hooks/useAllocationWarehouseMap"
import { useSkuMap } from "~/hooks/useVariants"
import { STORE_FORMATS, STORE_SIZES } from "~/utils/allocationStores"
import { api, type RouterInputs, type RouterOutputs } from "~/utils/api"
import { currentDateTimeUTC } from "~/utils/datetime"
import { RULE_DEFAULTS } from "~/utils/replenishmentRules"
import {
  createLineHydrator,
  memoizeByReference,
  type HydratedLine,
  type ReplenishmentSkuDetails,
} from "~/utils/replenishmentLines"
import type {
  CartItem,
  PackageFolder,
  RangePackage,
  Store,
  StoreClassification,
  Transfer,
} from "./types"

/**
 * Adapter between the allocation router and the prototype's mock-shaped
 * types, so the tab components keep their props/shape while the data becomes
 * real. Fields cut in review render as null/0 here: salesVsTarget (no target
 * data exists), lat/lng (map placeholder), fake pricing.
 */

type RouterStore = RouterOutputs["allocation"]["getStores"][number]

export type AllocationStatus = RouterStore["allocation"]

export type StoreWithStatus = Store & {
  /** The AllocationLocation id, which store x SKU settings and stock are keyed on. */
  allocationLocationId: string
  /** The class a fill scope groups the store under: its grade, or Outlet. */
  storeClass: StoreClassification
  allocation: AllocationStatus
  rateOfSalePerDay: number
  daysOfStock: number | null
  orphaned: boolean
  warehouseId: string | null
  zone: string | null
  replenishmentRuleId: string | null
  leadTimeDays: number
}

// Cart items carry sku-level lines (transfers are sku-grain); the product
// level quantity shown in the drawer is their sum.
export type CartItemWithLines = CartItem & {
  lines: { skuId: string; quantity: number; resultLineId: string | null }[]
}

const storeTypeOf = (format: string | null): Store["type"] =>
  STORE_FORMATS.find((option) => option.value === format)?.label ?? "Partner"

const isStoreSize = (value: string | null): value is Store["size"] =>
  (STORE_SIZES as readonly (string | null)[]).includes(value)

/** Outlets are a store type, and saving a store writes their grade as none, so read both. */
export function storeClassOf(store: Pick<Store, "type" | "classification">): StoreClassification {
  return store.type === "Outlet" || store.classification === "Outlet"
    ? "Outlet"
    : store.classification
}

function mapStore(store: RouterStore): StoreWithStatus {
  const { inTransit } = store
  const type = storeTypeOf(store.storeType)
  const classification: StoreClassification =
    store.grade === "outlet" ? "Outlet" : ((store.grade ?? "D") as StoreClassification)
  return {
    id: store.id,
    allocationLocationId: store.allocationLocationId,
    storeNumber: store.name,
    city: store.city ?? "",
    country: store.country ?? "",
    size: isStoreSize(store.sizeBand) ? store.sizeBand : "Medium",
    floorArea: store.floorArea ?? 0,
    type,
    classification,
    storeClass: storeClassOf({ type, classification }),
    sales3mo: Math.round(store.salesWindow),
    rateOfSale: Math.round(store.allocation.rateOfSale),
    stockCover: store.allocation.stockCover ?? 0,
    sellThrough: Math.round((store.allocation.sellThrough ?? 0) * 100),
    salesVsTarget: null, // cut: no store-level target data exists
    inTransit,
    rateOfSalePerDay: store.rateOfSalePerDay,
    daysOfStock:
      store.rateOfSalePerDay > 0 ? (store.storeStock + inTransit) / store.rateOfSalePerDay : null,
    storeStock: store.storeStock,
    capacity: store.capacityUnits,
    replenishmentInterval: `${store.replenishmentIntervalWeeks} weeks`,
    transitionTime: `${store.transitionTimeDays} days`,
    isNewStore: store.isNewStore,
    proxyStoreId: store.proxyStoreId,
    address: store.address ?? "",
    lat: 0, // cut: map placeholder only
    lng: 0,
    allocation: store.allocation,
    orphaned: store.orphaned,
    warehouseId: store.warehouseId,
    zone: store.zone,
    replenishmentRuleId: store.replenishmentRuleId,
    leadTimeDays: store.transitionTimeDays,
  }
}

export function useAllocationStores() {
  const storesQuery = api.allocation.getStores.useQuery()
  const stores = useMemo(() => (storesQuery.data ?? []).map(mapStore), [storesQuery.data])
  return { stores, isLoading: storesQuery.isLoading }
}

export function useRuleImpactLines(storeIds: string[]) {
  const linesQuery = api.allocation.getRuleImpactLines.useQuery(
    { storeIds },
    { placeholderData: keepPreviousData, enabled: storeIds.length > 0 },
  )
  return {
    lines: linesQuery.data ?? [],
    isLoading: linesQuery.isLoading,
    isError: linesQuery.isError,
  }
}

type ReplenishmentListLine = RouterOutputs["allocation"]["listReplenishmentLines"]["lines"][number]

export type ReplenishmentLine = HydratedLine<ReplenishmentListLine>

const NO_LINES: ReplenishmentLine[] = []

function skuStoreDetails(
  entry: ReturnType<ReturnType<typeof useSkuMap>["get"]>,
): ReplenishmentSkuDetails | undefined {
  if (!entry) return undefined
  const { variant, size } = entry
  return {
    productId: variant.productId,
    productName: variant.productName,
    variantName: variant.variantName ?? null,
    size,
    category: variant.category ?? null,
    color: variant.color ?? null,
    imageUrl: variant.imageUrl ?? null,
  }
}

export type ReplenishmentGroupBy = "none" | "store" | "product" | "category" | "package"

export function useReplenishmentLineList(
  input: RouterInputs["allocation"]["listReplenishmentLines"],
  extraDataFields: string[],
) {
  const pageQuery = api.allocation.listReplenishmentLines.useQuery(input, {
    placeholderData: keepPreviousData,
  })
  const storeMap = useAllocationStoreMap()
  const warehouseMap = useAllocationWarehouseMap()
  const inTransitMap = useAllocationInTransitMap()
  const { rules, isLoading: rulesLoading } = useReplenishmentRules()
  const skuMap = useSkuMap()
  const now = useMemo(() => currentDateTimeUTC(), [])
  const page = pageQuery.data
  const skuIds = useMemo(() => Object.keys(page?.skuById ?? {}), [page?.skuById])
  const salesMap = useAllocationSalesMap(skuIds)
  const inventoryMap = useAllocationInventoryMap(skuIds)
  const extraDataMap = useAllocationSkuExtraDataMap(skuIds, extraDataFields)
  const hydrateLine = useMemo(() => {
    const hydrate = createLineHydrator({
      lookups: {
        storeById: storeMap.data ?? {},
        warehouseNameById: warehouseMap.data ?? {},
        inTransitByLocationSku: inTransitMap.data ?? {},
      },
      sales: salesMap.data ?? {},
      inventory: inventoryMap.data ?? {},
      extraData: extraDataMap.data ?? {},
      page: {
        skuById: page?.skuById ?? {},
        skuDetailsById: page?.skuDetailsById ?? {},
        packageNameById: page?.packageNameById ?? {},
        packageCutoffById: page?.packageCutoffById ?? {},
        variantCutoffByKey: page?.variantCutoffByKey ?? {},
        currency: page?.currency ?? "",
        rateOfSaleWindowDays: page?.rateOfSaleWindowDays ?? RULE_DEFAULTS.rosWindowDays,
        noRecentSalesDays: page?.noRecentSalesDays ?? 0,
        referenceRosLevels: page?.referenceRosLevels ?? [],
        referenceRosWarnLevel: page?.referenceRosWarnLevel ?? null,
      },
      ruleById: new Map(rules.map((rule) => [rule.id, rule])),
      now,
      skuDetails: (skuId) => skuStoreDetails(skuMap.get(skuId)),
    })
    return memoizeByReference((line: ReplenishmentListLine): ReplenishmentLine => hydrate(line))
  }, [
    storeMap.data,
    warehouseMap.data,
    inTransitMap.data,
    salesMap.data,
    inventoryMap.data,
    extraDataMap.data,
    page?.skuById,
    page?.skuDetailsById,
    page?.packageNameById,
    page?.packageCutoffById,
    page?.variantCutoffByKey,
    page?.currency,
    page?.rateOfSaleWindowDays,
    page?.noRecentSalesDays,
    page?.referenceRosLevels,
    page?.referenceRosWarnLevel,
    rules,
    skuMap,
    now,
  ])
  const hasSkus = skuIds.length > 0
  const mapsReady =
    !hasSkus ||
    (salesMap.data !== undefined &&
      inventoryMap.data !== undefined &&
      !salesMap.isPlaceholderData &&
      !inventoryMap.isPlaceholderData)
  const mapsError = salesMap.error ?? inventoryMap.error
  const lines = useMemo(
    () => (mapsReady ? (page?.lines ?? []).map(hydrateLine) : NO_LINES),
    [mapsReady, page?.lines, hydrateLine],
  )
  const pageLoaded = page !== undefined
  const countQuery = api.allocation.countReplenishmentLinesWithTotal.useQuery(
    {
      search: input.search,
      searchBy: input.searchBy,
      filters: input.filters,
      includeZeroSuggested: input.includeZeroSuggested,
    },
    { placeholderData: keepPreviousData, enabled: pageLoaded },
  )
  return {
    lines,
    extraDataReady:
      !hasSkus ||
      extraDataFields.length === 0 ||
      (extraDataMap.data !== undefined && !extraDataMap.isPlaceholderData),
    error: pageQuery.error ?? mapsError,
    totalLineCount: countQuery.data?.filtered ?? 0,
    unfilteredLineCount: countQuery.data?.total ?? 0,
    isLoading:
      pageQuery.isLoading ||
      pageQuery.isPlaceholderData ||
      rulesLoading ||
      (!mapsReady && mapsError == null) ||
      storeMap.isLoading ||
      warehouseMap.isLoading ||
      inTransitMap.isLoading,
  }
}

export type ReplenishmentRuleWithMeta = RouterOutputs["allocation"]["getRules"][number]

export function useReplenishmentRules() {
  const rulesQuery = api.allocation.getRules.useQuery()
  return { rules: rulesQuery.data ?? [], isLoading: rulesQuery.isLoading }
}

export type RouteGraph = RouterOutputs["allocation"]["getRouteGraph"]

export function useRouteGraph() {
  const query = api.allocation.getRouteGraph.useQuery()
  return { graph: query.data ?? null, isLoading: query.isLoading }
}

export type RateOfSaleSettings = RouterOutputs["allocation"]["getRateOfSaleSettings"]

export function useRateOfSaleSettings() {
  const query = api.allocation.getRateOfSaleSettings.useQuery()
  return { settings: query.data ?? null, isLoading: query.isLoading }
}

export type SalesWindowEnds = RouterOutputs["allocation"]["getSalesWindowEnds"]

const NO_WINDOW_ENDS: SalesWindowEnds = { sales: null, rateOfSale: null }

export function useSalesWindowEnds() {
  return api.allocation.getSalesWindowEnds.useQuery().data ?? NO_WINDOW_ENDS
}

export function useRateOfSaleMutations() {
  const utils = api.useUtils()
  return {
    saveSettings: api.allocation.saveRateOfSaleSettings.useMutation({
      // Written into the cache before the caller clears its draft, so the page never shows the old values.
      onSuccess: (_result, input) => {
        utils.allocation.getRateOfSaleSettings.setData(undefined, (old) =>
          old ? { ...old, ...input } : old,
        )
        void utils.allocation.getRateOfSaleSettings.invalidate()
        void utils.allocation.listReplenishmentLines.invalidate()
      },
    }),
  }
}

export function useRuleMutations() {
  const utils = api.useUtils()
  const invalidate = () =>
    Promise.all([
      utils.allocation.getRules.invalidate(),
      utils.allocation.getStores.invalidate(),
      utils.allocation.getStoreSetup.invalidate(),
    ])
  return {
    saveRule: api.allocation.saveRule.useMutation({ onSuccess: invalidate }),
    duplicateRule: api.allocation.duplicateRule.useMutation({ onSuccess: invalidate }),
    deleteRule: api.allocation.deleteRule.useMutation({ onSuccess: invalidate }),
  }
}

export function useAllocationPackages() {
  const packagesQuery = api.allocation.getPackages.useQuery()
  const packages = useMemo(
    () =>
      (packagesQuery.data ?? []).map(
        (pkg): RangePackage => ({
          id: pkg.id,
          name: pkg.name,
          status: pkg.status,
          type: pkg.type,
          folderId: pkg.folderId,
          datesFolderId: pkg.datesFolderId,
          storesFolderId: pkg.storesFolderId,
          ownDeactivated: pkg.ownDeactivated,
          productIds: pkg.variantIds,
          skuIds: pkg.skuIds,
          storeIds: pkg.storeIds,
          startDate: pkg.startDate?.toISOString().slice(0, 10) ?? null,
          cutoffDate: pkg.cutoffDate?.toISOString().slice(0, 10) ?? null,
          ownDatesVariantCount: pkg.ownDatesVariantCount,
          overlap: pkg.overlap,
        }),
      ),
    [packagesQuery.data],
  )
  return { packages, isLoading: packagesQuery.isLoading }
}

const dayOf = (date: Date | null) => date?.toISOString().slice(0, 10) ?? null

export function useAllocationPackageFolders() {
  const foldersQuery = api.allocation.getPackageFolders.useQuery()
  const folders = useMemo(
    () =>
      (foldersQuery.data ?? []).map(
        (folder): PackageFolder => ({
          ...folder,
          ownStartDate: dayOf(folder.ownStartDate),
          ownCutoffDate: dayOf(folder.ownCutoffDate),
          startDate: dayOf(folder.startDate),
          cutoffDate: dayOf(folder.cutoffDate),
        }),
      ),
    [foldersQuery.data],
  )
  return { folders, isLoading: foldersQuery.isLoading }
}

const TRANSFER_STATUS_LABEL: Record<string, Transfer["status"]> = {
  pending: "Open",
  completed: "Received",
  cancelled: "Cancelled",
}

export function useAllocationTransfers() {
  const transfersQuery = api.allocation.getTransfers.useQuery()
  const transfers = useMemo(
    () =>
      (transfersQuery.data ?? []).map(
        (transfer): Transfer => ({
          id: transfer.id,
          storeId: transfer.storeId ?? "",
          storeNumber: transfer.storeName,
          status: TRANSFER_STATUS_LABEL[transfer.status] ?? "Open",
          productCount: transfer.productCount,
          unitCount: transfer.unitCount,
          createdDate: transfer.createdAt.toISOString().slice(0, 10),
          expectedDelivery: transfer.expectedDelivery?.toISOString().slice(0, 10) ?? "—",
        }),
      ),
    [transfersQuery.data],
  )
  return { transfers, isLoading: transfersQuery.isLoading }
}

// Every client disables "Generate suggested qty" while any run is in progress.
// NOTIFY events refresh the status when a run starts and ends; the slow poll
// covers a missed event.
export function useSuggestedQuantityRunStatus() {
  const status = api.replanishment.getSuggestedQuantityStatus.useQuery(undefined, {
    refetchInterval: (query) => (query.state.data?.running ? 30_000 : false),
  })
  return { running: status.data?.running ?? false, runningSince: status.data?.runningSince ?? null }
}

export function useAllocationMutations() {
  const utils = api.useUtils()
  const invalidate = () => utils.allocation.invalidate()
  const invalidatePackages = () =>
    Promise.all([
      utils.allocation.getPackages.invalidate(),
      utils.allocation.getPackageFolders.invalidate(),
      utils.allocation.getStoreTimeline.invalidate(),
    ])
  return {
    // A started run reports back over LISTEN/NOTIFY; only an inline one refreshes here.
    generateSuggestedQuantity: api.replanishment.generateSuggestedQuantity.useMutation({
      onSuccess: (result) => {
        void utils.replanishment.getSuggestedQuantityStatus.invalidate()
        if (!result.running) void invalidate()
      },
    }),
    saveOverrides: api.allocation.saveOverrides.useMutation({
      onSuccess: () => utils.allocation.getStoreDetail.invalidate(),
    }),
    createTransfers: api.allocation.createTransfers.useMutation({ onSuccess: invalidate }),
    updateTransferStatus: api.allocation.updateTransferStatus.useMutation({
      onSuccess: invalidate,
    }),
    savePackage: api.allocation.savePackage.useMutation({
      onSuccess: invalidatePackages,
    }),
    savePackagePage: api.allocation.savePackagePage.useMutation({
      onSuccess: invalidatePackages,
    }),
    savePackageFolder: api.allocation.savePackageFolder.useMutation({
      onSuccess: invalidatePackages,
    }),
  }
}
